import React, { useContext, useRef, useState } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { useForm } from "react-hook-form";
import { navigate } from "@reach/router";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { Calendar } from "react-feather";
import { FormErrors, Modal } from "@squirrellearningltd/go-teacher-components";
import Header from "../../../components/Header";
import BackLink from "../../../components/BackLink";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE_SCHOOL_FOR_EDIT, EDIT_FRANCHISE_SCHOOL, DEACTIVATE_FRANCHISE_SCHOOL } from "../services/graphql";
import styles from "../Franchises.module.scss";

const EditFranchiseSchool = ({ schoolId, franchiseId, portal = false }) => {
  const { currentUser } = useContext(AuthContext);
  const [formErrors, setFormErrors] = useState([]);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false);
  const [deactivateErrors, setDeactivateErrors] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const startPicker = useRef(null);
  const endPicker = useRef(null);
  const saving = useRef(false);
  const schoolPath = portal ? `/franchise-portal/${franchiseId}/schools/${schoolId}` : `/franchises/${franchiseId}/schools/${schoolId}`;
  const schema = Yup.object().shape({
    name: Yup.string().trim(),
    address: Yup.string().trim(),
    city: Yup.string().trim(),
    contactName: Yup.string().trim(),
    contactEmail: Yup.string().trim().email("Please enter a valid email address"),
    annualPupilLicences: Yup.number().nullable().transform((value, originalValue) => originalValue === "" ? null : value).typeError("Please enter a number").integer("Please enter a whole number")
  });
  const { register, handleSubmit, errors, reset, watch } = useForm({
    validationSchema: schema,
    mode: "onSubmit"
  });
  const { data, loading, error } = useQuery(GET_FRANCHISE_SCHOOL_FOR_EDIT, {
    variables: { schoolId },
    fetchPolicy: "no-cache",
    onCompleted: data => {
      const school = data.school;
      if (!school) return;
      reset({
        name: school.name || "",
        address: school.address || "",
        city: school.city || "",
        contactName: school.contactName || "",
        contactEmail: school.contactEmail || "",
        annualPupilLicences: school.annualPupilLicences == null ? "" : school.annualPupilLicences
      });
      setStartDate(school.licencePeriodStart ? moment(school.licencePeriodStart, "YYYY-MM-DD").toDate() : null);
      setEndDate(school.licencePeriodEnd ? moment(school.licencePeriodEnd, "YYYY-MM-DD").toDate() : null);
    }
  });
  const [updateFranchiseSchool, { loading: updating }] = useMutation(EDIT_FRANCHISE_SCHOOL);
  const [deactivateFranchiseSchool, { loading: deactivating }] = useMutation(DEACTIVATE_FRANCHISE_SCHOOL);
  const school = data && data.school;
  const belongsToFranchise = school && school.franchise && String(school.franchise.id) === String(franchiseId);
  const assigned = (school && school.assignedPupilLicences) || 0;
  const available = Math.max(0, (Number(watch("annualPupilLicences")) || 0) - assigned);

  const onSubmit = values => {
    if (saving.current || showDeactivate || !belongsToFranchise) return;
    saving.current = true;
    setFormErrors([]);
    updateFranchiseSchool({
      variables: {
        schoolId,
        ...values,
        licenceStartDate: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
        licenceEndDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null
      }
    }).then(({ data }) => {
      const result = data && data.updateFranchiseSchool;
      if (result && result.errors && result.errors.length > 0) {
        setFormErrors(result.errors);
      } else if (result && result.school && result.school.id) {
        navigate(schoolPath);
        return;
      } else {
        setFormErrors(["Unable to save the school. Please try again."]);
      }
      saving.current = false;
    }).catch(error => {
      saving.current = false;
      setFormErrors([error.message]);
    });
  };

  const closeDeactivate = () => {
    if (saving.current) return;
    setShowDeactivate(false);
    setDeactivateConfirmed(false);
    setDeactivateErrors([]);
  };

  const handleDeactivate = () => {
    if (!deactivateConfirmed || saving.current || !belongsToFranchise || school.onboardingStatus !== "active") return;
    saving.current = true;
    setDeactivateErrors([]);
    deactivateFranchiseSchool({ variables: { schoolId } }).then(({ data }) => {
      const result = data && data.deactivateFranchiseSchool;
      if (result && result.errors && result.errors.length > 0) {
        setDeactivateErrors(result.errors);
      } else if (result && result.school && result.school.id) {
        navigate(schoolPath);
        return;
      } else {
        setDeactivateErrors(["Unable to deactivate the school. Please try again."]);
      }
      saving.current = false;
    }).catch(error => {
      saving.current = false;
      setDeactivateErrors([error.message]);
    });
  };

  return (
    <div className={portal ? `${styles.franchises} ${styles.portal}` : styles.franchises}>
      {portal && <Header user={currentUser} portalFranchise={belongsToFranchise ? school.franchise : null} portalFranchiseId={franchiseId} portalPage="schools" />}
      <BackLink text={belongsToFranchise ? school.name : "School"} to={schoolPath} />
      {loading && <p role="status">Loading school...</p>}
      {error && <p role="alert">Unable to load school. Please try again later.</p>}
      {!loading && !error && !belongsToFranchise && <p>School not found in this franchise.</p>}
      {!loading && !error && belongsToFranchise && <>
        <div className={`${styles.header} ${styles.schoolFormHeading}`}>
          <h1 className="title">Edit school</h1>
          <p>Update school details and manage the current licence allocation.</p>
        </div>
        <form className={`${styles.territoryForm} ${styles.schoolForm}`} onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.detailPanel}>
            <h2>School details</h2>
            <div className={styles.schoolNameRow}>
              <div>
                <label htmlFor="name" className="basic-form__text-label">School name</label>
                <input id="name" name="name" type="text" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.name && <p className="basic-form__hint u-m-top-0">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="schoolCode" className="basic-form__text-label">School code</label>
                <input id="schoolCode" className="basic-form__text-box" value={school.schoolCode || ""} readOnly />
              </div>
            </div>
            <div className={styles.schoolFormTwoColumns}>
              <div>
                <label htmlFor="address" className="basic-form__text-label">Address line 1</label>
                <input id="address" name="address" type="text" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.address && <p className="basic-form__hint u-m-top-0">{errors.address.message}</p>}
              </div>
              <div>
                <label htmlFor="city" className="basic-form__text-label">City / Governorate</label>
                <input id="city" name="city" type="text" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.city && <p className="basic-form__hint u-m-top-0">{errors.city.message}</p>}
              </div>
            </div>
            <div className={styles.schoolFormTwoColumns}>
              <div>
                <label htmlFor="contactName" className="basic-form__text-label">Contact name</label>
                <input id="contactName" name="contactName" type="text" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.contactName && <p className="basic-form__hint u-m-top-0">{errors.contactName.message}</p>}
              </div>
              <div>
                <label htmlFor="contactEmail" className="basic-form__text-label">Contact email</label>
                <input id="contactEmail" name="contactEmail" type="email" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.contactEmail && <p className="basic-form__hint u-m-top-0">{errors.contactEmail.message}</p>}
              </div>
            </div>
          </div>
          <div className={`${styles.detailPanel} ${styles.section}`}>
            <h2>Current pupil licence allocation</h2>
            <p className={styles.schoolFormDescription}>Changes are recorded against this annual period. No financial calculation is shown yet.</p>
            <div className={styles.schoolFormThreeColumns}>
              <div>
                <label htmlFor="annualPupilLicences" className="basic-form__text-label">Pupil licences</label>
                <input id="annualPupilLicences" name="annualPupilLicences" type="number" step="1" className="basic-form__text-box" ref={register} disabled={updating} />
                {errors.annualPupilLicences && <p className="basic-form__hint u-m-top-0">{errors.annualPupilLicences.message}</p>}
              </div>
              <div>
                <label htmlFor="periodStart" className="basic-form__text-label">Period start</label>
                <div className={styles.licenceDate}>
                  <DatePicker id="periodStart" ref={startPicker} selected={startDate} onChange={date => setStartDate(date)} dateFormat="d MMMM yyyy" className="basic-form__text-box" wrapperClassName={styles.datePickerWrapper} disabled={updating} />
                  <button type="button" aria-label="Choose period start" disabled={updating} onClick={() => startPicker.current.setOpen(true)}><Calendar size={18} aria-hidden="true" /></button>
                </div>
              </div>
              <div>
                <label htmlFor="periodEnd" className="basic-form__text-label">Period end</label>
                <div className={styles.licenceDate}>
                  <DatePicker id="periodEnd" ref={endPicker} selected={endDate} onChange={date => setEndDate(date)} dateFormat="d MMMM yyyy" className="basic-form__text-box" wrapperClassName={styles.datePickerWrapper} disabled={updating} />
                  <button type="button" aria-label="Choose period end" disabled={updating} onClick={() => endPicker.current.setOpen(true)}><Calendar size={18} aria-hidden="true" /></button>
                </div>
              </div>
            </div>
            <p className={styles.schoolFormNotice}>{assigned.toLocaleString("en-GB")} assigned · {available.toLocaleString("en-GB")} available</p>
          </div>
          {formErrors.length > 0 && <FormErrors errors={formErrors} />}
          <div className={styles.formActions}>
            <button type="button" className={styles.button} disabled={updating} onClick={() => navigate(schoolPath)}>Cancel</button>
            <button type="submit" className={styles.primaryButton} disabled={updating}>{updating ? "Saving..." : "Save changes"}</button>
          </div>
        </form>
        {school.onboardingStatus === "active" && <div className={styles.deactivateSchool}>
          <div><h3>Deactivate school</h3><p>Stops new access without deleting historical records.</p></div>
          <button type="button" className={styles.button} disabled={updating || deactivating} onClick={() => setShowDeactivate(true)}>Deactivate</button>
        </div>}
        {showDeactivate && school.onboardingStatus === "active" && (
          <Modal closeModal={closeDeactivate}>
            <div className={styles.deactivateModal} role="dialog" aria-modal="true" aria-labelledby="deactivateSchoolTitle">
              <h2 id="deactivateSchoolTitle">Deactivate this school?</h2>
              <div className={styles.deactivateWarning}>
                <h3>{school.name} will lose new access</h3>
                <p>Historical pupils, reading logs and licence records will be retained.</p>
              </div>
              <label className={styles.deactivateConfirmation}>
                <input type="checkbox" checked={deactivateConfirmed} onChange={event => setDeactivateConfirmed(event.target.checked)} disabled={deactivating} />
                <span>I understand that staff, parents and pupils will no longer be able to sign in.</span>
              </label>
              {deactivateErrors.length > 0 && <FormErrors errors={deactivateErrors} />}
              <div className={styles.formActions}>
                <button type="button" className={styles.button} disabled={deactivating} onClick={closeDeactivate}>Cancel</button>
                <button type="button" className={`${styles.button} ${styles.deactivateButton}`} disabled={!deactivateConfirmed || deactivating} onClick={handleDeactivate}>{deactivating ? "Deactivating..." : "Deactivate"}</button>
              </div>
            </div>
          </Modal>
        )}
      </>}
    </div>
  );
};

export default EditFranchiseSchool;
